import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  rolloutSignalQueryOptions,
  servedVersionQueryOptions,
  serverStatusQueryOptions,
  versionAnnouncementQueryOptions,
} from "@/lib/queries";
import { useServerStatusStore } from "@/hooks/serverStatusStore";

const SEEN_ANNOUNCEMENT_STORAGE_KEY = "nexus_seen_announcement_id";

/**
 * `refresh` — this client runs an older bundle, so reloading gets new code.
 * `notice` — the bundle is already current; the announcement is informational.
 */
export type VersionNoticeMode = "refresh" | "notice";

/** `null` means this browser has never recorded an announcement id. */
function readSeenAnnouncementId(): string | null {
  try {
    return localStorage.getItem(SEEN_ANNOUNCEMENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSeenAnnouncementId(id: string): void {
  try {
    localStorage.setItem(SEEN_ANNOUNCEMENT_STORAGE_KEY, id);
  } catch {
    /* private browsing — in-memory acknowledgement still applies */
  }
}

/**
 * Drives the version notice from two independent signals:
 * SHA mismatch (this client is stale) and a broadcast announcement id (an
 * admin announced, regardless of which bundle the client runs). The id is what
 * lets users already on the newest bundle be notified at all.
 */
export function useVersionAnnouncement() {
  const { data } = useQuery(serverStatusQueryOptions());
  const { data: servedVersion } = useQuery(servedVersionQueryOptions());
  const { data: rolloutSignal } = useQuery(rolloutSignalQueryOptions());
  const serverStatus = useServerStatusStore((s) => s.status);
  const [seenAnnouncementId, setSeenAnnouncementId] = useState(
    readSeenAnnouncementId,
  );

  const announcementId = data?.announcementId;
  const servedSha = servedVersion?.sha;
  const bakedSha = window.__NEXUS_SHA__ ?? "";
  const isOffline = serverStatus === "offline";

  // A fresh browser adopts whatever is current on its first healthy poll, so a
  // new session never replays an announcement made before it started. The
  // baseline is recorded even when nothing has been announced yet (empty
  // string) — otherwise the very first announcement would look like a first
  // sighting and be swallowed.
  const hasBaseline = seenAnnouncementId !== null;
  const hasHealthData = Boolean(data);
  useEffect(() => {
    if (hasBaseline || !hasHealthData) return;
    const baseline = announcementId ?? "";
    writeSeenAnnouncementId(baseline);
    setSeenAnnouncementId(baseline);
  }, [hasBaseline, hasHealthData, announcementId]);

  // Compared against the web tier, never the API. `api` and `app` are separate
  // Deployments that roll independently, so during a staggered rollout the API
  // can report a SHA this tier is not serving yet — a mismatch no reload could
  // ever clear, which is exactly what left users stuck (AD-2221).
  //
  // Gated on the tier having converged, because `/version.json` is answered by
  // whichever pod the load balancer picked. Mid-rollout one pod can advertise
  // the new build while a reload lands on a sibling still serving the old one,
  // which is the same dead end a pod further down the rollout. Only an explicit
  // `false` suppresses the prompt: an unreadable signal must not disable
  // updates altogether.
  const isMidRollout = rolloutSignal?.isWebTierConverged === false;
  const hasShaMismatch = Boolean(
    servedSha && bakedSha && servedSha !== bakedSha && !isMidRollout,
  );
  const hasNewAnnouncement = Boolean(
    hasBaseline && announcementId && announcementId !== seenAnnouncementId,
  );

  const { data: announcement } = useQuery(
    versionAnnouncementQueryOptions(
      hasNewAnnouncement && !isOffline ? announcementId : undefined,
    ),
  );

  const acknowledge = useCallback(() => {
    if (!announcementId) return;
    writeSeenAnnouncementId(announcementId);
    setSeenAnnouncementId(announcementId);
  }, [announcementId]);

  const mode: VersionNoticeMode = hasShaMismatch ? "refresh" : "notice";

  return {
    shouldShow: (hasShaMismatch || hasNewAnnouncement) && !isOffline,
    mode,
    servedSha,
    announcementId,
    releaseNotes: announcement?.releaseNotes ?? null,
    acknowledge,
    refresh: () => window.location.reload(),
  };
}
